import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '../../../features/auth';
import { MainSidebar, useSidebar } from '../../../widgets/main-sidebar';
import { Topbar } from '../../../widgets/topbar';
import { fetchLeaderBoard } from '../../../entities/leaderBoard';
import type { LeaderBoardEntry, LeaderBoardGroup, LeaderBoardResponse } from '../../../entities/leaderBoard';
import { Select } from '../../../shared/ui/Select';
import { Button } from '../../../shared/ui/Button';

type Tab = 'managers' | 'groups';

const now = new Date();

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = String(now.getFullYear() - i);
  return { value: y, label: y };
});

const MONTH_OPTIONS = [
  { value: '1', label: 'Январь' },
  { value: '2', label: 'Февраль' },
  { value: '3', label: 'Март' },
  { value: '4', label: 'Апрель' },
  { value: '5', label: 'Май' },
  { value: '6', label: 'Июнь' },
  { value: '7', label: 'Июль' },
  { value: '8', label: 'Август' },
  { value: '9', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
];

const MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

const LeaderBoardPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { toggle } = useSidebar();

  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [tab, setTab] = useState<Tab>('managers');

  const resetFilters = () => {
    setYear(String(now.getFullYear()));
    setMonth(String(now.getMonth() + 1));
  };

  const { data, isLoading } = useQuery<LeaderBoardResponse>({
    queryKey: ['leaderBoard', year, month],
    queryFn: () => fetchLeaderBoard({ year, month }),
  });

  const entries: (LeaderBoardEntry | LeaderBoardGroup)[] = tab === 'managers' ? (data?.items ?? []) : (data?.groups ?? []);
  const updatedAt = data?.updatedAt
    ? new Date(data.updatedAt).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  if (!currentUser) return null;

  const maxCount = entries[0]?.count || 1;

  const renderRow = (entry: LeaderBoardEntry | LeaderBoardGroup) => {
    const pct = Math.max(Math.round((entry.count / maxCount) * 100), 3);
    const medal = MEDAL[entry.order];
    return (
      <div key={entry.id} className={`lb-list-row${medal ? ` lb-list-row-top${entry.order}` : ''}`}>
        <div className={`lb-list-rank${medal ? ` lb-list-rank-medal` : ''}`}>
          {medal ?? entry.order}
        </div>
        <div className={`lb-list-avatar${medal ? ` lb-list-avatar-top${entry.order}` : ''}`}>
          {getInitials(entry.name)}
        </div>
        <div className="lb-list-name">{entry.name}</div>
        <div className="lb-list-bar-wrap">
          <div className={`lb-list-bar${medal ? ` lb-list-bar-top${entry.order}` : ''}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="lb-list-count">{entry.count}</div>
      </div>
    );
  };

  return (
    <div id="app-screen">
      <Topbar user={currentUser} onLogout={logout} onSidebarToggle={toggle} />
      <div className="app-body">
        <MainSidebar isAdmin={currentUser.role === 'ADMIN'} isManager={currentUser.role === 'MANAGER'} />
        <div className="app-content adm-content">
          <div className="adm-section">
            <div className="adm-section-header">
              <h2 className="adm-section-title">Лидер борд</h2>
              <p className="adm-section-sub">Рейтинг сотрудников по сделкам</p>
            </div>

            <div className="adm-analytics-filters">
              <div className="adm-analytics-period">
                <Select options={MONTH_OPTIONS} value={month} onChange={setMonth} placeholder="Месяц" />
              </div>
              <div className="adm-analytics-period">
                <Select options={YEAR_OPTIONS} value={year} onChange={setYear} placeholder="Год" />
              </div>
              <Button variant="adm-cancel" type="button" onClick={resetFilters}>
                <IconX size={14} stroke={2} />Сбросить
              </Button>
              {updatedAt && (
                <span className="adm-analytics-updated">Обновлено: {updatedAt}</span>
              )}
            </div>

            <div className="lb-tabs">
              <button
                type="button"
                className={`lb-tab${tab === 'managers' ? ' lb-tab-active' : ''}`}
                onClick={() => setTab('managers')}
              >
                Менеджеры
              </button>
              <button
                type="button"
                className={`lb-tab${tab === 'groups' ? ' lb-tab-active' : ''}`}
                onClick={() => setTab('groups')}
              >
                Группы
              </button>
            </div>

            {isLoading ? (
              <div className="lb-loading">Загрузка...</div>
            ) : entries.length === 0 ? (
              <div className="lb-empty-state">Нет данных за выбранный период</div>
            ) : (
              <div className="lb-content">
                <div className="lb-list">
                  {entries.map(renderRow)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { LeaderBoardPage };
